import { ChangeDetectionStrategy, Component, computed, effect, input, model } from '@angular/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';

@Component({
  selector: 'dss-libs-ui-paginator',
  imports: [MatMenu, MatMenuItem, MatMenuTrigger, MatIcon, TranslocoDirective, TranslocoDecimalPipe],
  templateUrl: './paginator.component.html',
  styleUrl: './paginator.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginatorComponent {
  readonly loading = input(false);
  readonly totalLength = input(0);
  readonly forceCompactView = input(false);
  readonly pageSizeOptions = input<number[]>([10, 25, 50, 100]);

  readonly pageSize = model(7);
  readonly currentPage = model(1);

  protected readonly totalPages = computed(() => Math.ceil(this.totalLength() / this.pageSize()));
  protected readonly from = computed(() => (this.totalLength() ? (this.currentPage() - 1) * this.pageSize() + 1 : 0));
  protected readonly to = computed(() => Math.min(this.from() + this.pageSize() - 1, this.totalLength()));

  protected readonly pages = computed<(number | null)[]>(() => {
    const totalPages = this.totalPages();
    const currentPage = this.currentPage();
    const pages: (number | null)[] = [];
    for (let page = 1; page <= totalPages; page++) {
      const isEdgePage = page === 1 || page === totalPages;
      const isAroundCurrentPage = page >= currentPage - 2 && page <= currentPage + 2;
      if (isEdgePage || isAroundCurrentPage) pages.push(page);
      else if (pages.at(-1) !== null) pages.push(null);
    }
    return pages;
  });

  protected readonly clampCurrentPage = effect(() => {
    const totalPages = this.totalPages();
    if (this.loading()) return;
    if (this.currentPage() > totalPages) this.currentPage.set(Math.max(totalPages, 1));
  });

  protected previousPage() {
    if (this.currentPage() === 1) return;
    this.currentPage.update((page) => page - 1);
  }

  protected nextPage() {
    if (this.currentPage() === this.totalPages()) return;
    this.currentPage.update((page) => page + 1);
  }

  protected updatePage(page: number) {
    this.currentPage.set(page);
  }

  protected updatePageSize(pageSize: number) {
    if (pageSize === this.pageSize()) return;
    this.pageSize.set(pageSize);
    this.currentPage.set(1);
  }
}
